import { useContext } from 'react';
import {
    DefaultCartContext,
    DefaultCustomerInfo,
    IAccessoryItem,
    IAlternateContact,
    ICartProps,
    ICostSummaryItem,
    ICustomerAgreements,
    ICustomerInfo,
    ILineItem,
    IShippingMethod,
    IUseCartHook
} from './CartContext.types';
import { CartContext } from './CartContext';
import ValidatePromosApi from './Promo/ValidatePromos.api';
import { IValidatePromoResponse } from './Promo/Promo.types';
import { usePromo } from './Promo/Promo.hooks';
import {
    GetByodFilename,
    SaveCart,
    SaveFile,
    SendInternetOrderEmail,
    SendPurchaseSuccessEmail
} from './CartContext.api';
import { usePlansHooks } from '../../pages/Plans/PlansHooks';
import { generateAccessoryCosts } from './CostItemHooks/Cart/AccessoryCostHook';
import { generateShippingCosts } from './CostItemHooks/Cart/ShippingCostHook';
import { generateMonthlyDeviceCosts } from './CostItemHooks/MonthlyDeviceCostHooks';
import { generateDueTodayDeviceCosts } from './CostItemHooks/DueTodayDeviceCostHooks';
import { generatePlanCosts } from './CostItemHooks/PlanCostHooks';
import { ICreateCartResponse, IReservePhoneNumbersResponse } from '../../api/Idi/Idi.api.types';
import { useDownPaymentHook } from './CostItemHooks/Device/DownPaymentHook';
import { useAssurancePaymentHook } from './CostItemHooks/Device/AssurancePaymentHook';
import { useBundleDiscountHook } from './CostItemHooks/Device/BundleDiscountHook';

export const useCart = (): IUseCartHook => {
    const { cart, setCart, cartStepIndex, setCartStepIndex, reloadSession, isCartLoaded } =
        useContext(CartContext);

    const { calcBestPromos } = usePromo();

    const { GetLinePrice } = usePlansHooks();

    const confirmOrder = () => {
        SaveCart(cart.paymentConfirmationId, cart, () => {
            SendPurchaseSuccessEmail(cart);

            cart.lineItems.forEach((l) => {
                if (l.byod?.file) {
                    SaveFile({
                        file: l.byod.file,
                        name: GetByodFilename(l, cart.paymentConfirmationId)
                    });
                }
            });

            window.sessionStorage.removeItem('cart');
            handleUpdateCart(DefaultCartContext().cart);
        });

        return cart.paymentConfirmationId;
    };

    const confirmInternetOrder = () => {
        SendInternetOrderEmail(cart);
    };

    const updateCart = function (updatedCart: ICartProps) {
        if (setCart) setCart({ ...updatedCart });
    };

    const handleUpdateCart = function (updatedCart: ICartProps) {
        SyncLineItemData(updatedCart);
        updateValidPromos(updatedCart);
    };

    const stitchPromos = (updatedCart: ICartProps, response: IValidatePromoResponse) => {
        updatedCart.lineItems.forEach((li) => {
            li.promoResponses = [];
            delete li.promoSummary;

            response.lineItems.forEach((liR) => {
                if (li.lineNumber === liR.lineNumber) {
                    li.promoResponses?.push(liR);
                }
            });
        });

        calcBestPromos(updatedCart);
    };

    const updateValidPromos = function (updatedCart: ICartProps) {
        ValidatePromosApi.ValidatePromos(
            updatedCart,
            (response: IValidatePromoResponse) => {
                stitchPromos(updatedCart, response);
                recalculateCart(updatedCart);
                updateCart(updatedCart);
            },
            () => {
                updateCart(updatedCart);
            }
        );
    };

    const updateCartStepIndex = function (stepIndex: number) {
        reloadSession();
        setCartStepIndex(stepIndex);
    };

    const updateCustomerInfo = function (customerInfo: ICustomerInfo) {
        const updatedCart = {
            ...cart,
            customerInfo: customerInfo
        };
        handleUpdateCart(updatedCart);
    };

    const updateAlternateContact = function (alternateContact: IAlternateContact) {
        const updatedCart = {
            ...cart,
            alternateContact: alternateContact
        };
        handleUpdateCart(updatedCart);
    };

    const updateAgreements = function (agreements: ICustomerAgreements) {
        const updatedCart = {
            ...cart,
            agreements: agreements
        };
        handleUpdateCart(updatedCart);
    };

    const updateAccessories = function (accessories: IAccessoryItem[]) {
        const updatedCart = {
            ...cart,
            accessories: accessories
        };
        handleUpdateCart(updatedCart);
    };

    const updateLineItems = function (lineItems: ILineItem[]) {
        const updatedCart = {
            ...cart,
            lineItems: lineItems
        };

        handleUpdateCart(updatedCart);
    };

    const updateShippingMethod = function (method: IShippingMethod) {
        const updatedCart = {
            ...cart,
            shippingMethod: method,
            closestStoreTimeZone: method.address.timeZone
        };
        handleUpdateCart(updatedCart);
    };

    const updateAutopay = (isAutopay: boolean) => {
        const updatedCart = {
            ...cart,
            autopay: isAutopay
        };
        handleUpdateCart(updatedCart);
    };

    const updateEbill = (isEbill: boolean) => {
        const updatedCart = {
            ...cart,
            eBill: isEbill
        };
        handleUpdateCart(updatedCart);
    };

    const updateReservedNumbers = (reservedNumbers: IReservePhoneNumbersResponse) => {
        cart.reserveNumberResponse = reservedNumbers;
        handleUpdateCart(cart);
    };

    const setIdiCart = (idiCart: ICreateCartResponse) => {
        cart.idiCart = idiCart;
        handleUpdateCart(cart);
    };

    const setIsDuplicateCustomer = (isDuplicate: boolean) => {
        cart.isDuplicateCustomer = isDuplicate;
        handleUpdateCart(cart);
    };

    /**
     * Creates an ICostSummaryItem for every cost and discount in the cart, used for calculating
     * totals, populating Summary / Savings cards, etc.
     * @param updatedCart
     */
    const recalculateCart = (updatedCart: ICartProps) => {
        updatedCart.monthlySummary = [];
        updatedCart.dueTodaySummary = [];

        updatedCart.lineItems.forEach((l) => {
            calculateLineCostSummary(l, updatedCart);
        });

        calculateAdditionalCosts(updatedCart);

        updateCart(updatedCart);
    };

    /**
     * Generates ICostSummaryItem for every cost and discount in the cart.
     * @param lineItem
     * @param updatedCart
     */
    const calculateLineCostSummary = (lineItem: ILineItem, updatedCart: ICartProps) => {
        if (lineItem.planSelection?.plan) {
            generatePlanCosts(lineItem, updatedCart.autopay, updatedCart.monthlySummary);
        }

        // Add device related items
        if (lineItem.device) {
            // Installment plan
            if (lineItem.device.usePricingOptions) {
                generateMonthlyDeviceCosts(lineItem, updatedCart.monthlySummary);
            }

            // Pay full retail
            if (lineItem.device?.useRetailPrice) {
                generateDueTodayDeviceCosts(lineItem, updatedCart.dueTodaySummary);
            }

            const bundleDiscount = useBundleDiscountHook(lineItem);
            if (bundleDiscount) {
                updatedCart.monthlySummary.push(bundleDiscount);
            }

            const downPayment = useDownPaymentHook(lineItem);
            if (downPayment) {
                updatedCart.dueTodaySummary.push(downPayment);
            }

            const assurancePayment = useAssurancePaymentHook(lineItem);
            if (assurancePayment) {
                updatedCart.monthlySummary.push(assurancePayment);
            }
        }
    };

    const calculateAdditionalCosts = (updatedCart: ICartProps) => {
        if (updatedCart.accessories.length > 0) {
            updatedCart.accessories.forEach((a) => {
                updatedCart.dueTodaySummary.push(generateAccessoryCosts(a));
            });
        }

        const shippingCost = generateShippingCosts(updatedCart.shippingMethod);
        if (shippingCost) {
            updatedCart.dueTodaySummary.push(shippingCost);
        }

        if (updatedCart.idiCart) {
            // Handle specific fees
            updatedCart.fees = updatedCart.idiCart.items
                .filter((item) => item.sku.toLowerCase().endsWith('_fee') && item.totalPrice > 0)
                .map((fee, index) => {
                    // 1-based index for line numbers, used as a prefix for the itemName below.
                    const lineNumber = index + 1;
                    return {
                        amount: fee.totalPrice,
                        itemName: `Line ${lineNumber} - ${fee.description}`,
                        lineNumber: lineNumber,
                        sku: fee.sku,
                        type: 'cost' as const
                    };
                });

            const taxSummaryItem: ICostSummaryItem = {
                amount: updatedCart.idiCart.taxSubtotal,
                itemName: 'Taxes',
                lineNumber: 0,
                type: 'display',
                children: []
            };

            // Handle specific taxes
            const taxItems = updatedCart.idiCart.taxes
                .filter((tax) => tax.amount > 0 && !tax.isUseTax)
                .map((tax) => ({
                    itemName: `${tax.name} (${tax.taxRate.toFixed(2)}%)`,
                    amount: tax.amount,
                    lineNumber: 0,
                    type: 'cost' as const
                }));

            taxSummaryItem.children = taxItems;
            updatedCart.dueTodaySummary.push(taxSummaryItem);
        }
    };

    /**
     * Need to ensure as lines are added/deleted/modified that any affected data is synchronized to the new cart state
     * 1. Line Number reassignment
     * 2. Some plans are only available if the total selected line count is below a threshold (maxLines), so we clear those out
     * 3. Set the multi-line prices for the plan selections
     * @param updatedCart
     * @constructor
     */
    const SyncLineItemData = (updatedCart: ICartProps) => {
        for (let x = 0; x < updatedCart.lineItems.length; x++) {
            // 1
            const li = updatedCart.lineItems[x];
            li.lineNumber = x + 1;

            // 2
            const plan = updatedCart.lineItems[x - 1]?.planSelection?.plan;
            if (plan && plan.maxLines && updatedCart.lineItems.length > (plan.maxLines ?? 0)) {
                updatedCart.lineItems[x - 1].planSelection = undefined;
            }

            //3
            if (li.planSelection) {
                li.planSelection.linePrice = GetLinePrice(li.planSelection.plan, li.lineNumber);
            }

            updatedCart.lineItems[x] = li;
        }
    };

    const clearCart = () => {
        cart.lineItems = [];
        cart.customerInfo = DefaultCustomerInfo();
        cart.monthlySummary = [];
        cart.dueTodaySummary = [];
        setCartStepIndex(0);
        handleUpdateCart(cart);
    };

    return {
        cart,
        cartStepIndex,
        setCartStepIndex: updateCartStepIndex,
        reloadSession,
        confirmOrder,
        confirmInternetOrder,
        setCart,
        handleUpdateCart,
        updateCartStepIndex,
        updateCustomerInfo,
        updateAlternateContact,
        updateAgreements,
        updateAccessories,
        updateLineItems,
        updateShippingMethod,
        updateAutopay,
        updateEbill,
        updateReservedNumbers,
        setIdiCart,
        setIsDuplicateCustomer,
        SyncLineItemData,
        clearCart,
        isCartLoaded
    };
};
